'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { School, User, CreditCard, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft, Building2, Phone, Check } from 'lucide-react';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

type Step = 1 | 2 | 3 | 4;

interface RegistrationFormData {
  schoolName: string;
  schoolType: string;
  phase: string;
  emisNumber: string;
  province: string;
  district: string;
  streetAddress: string;
  suburb: string;
  city: string;
  postalCode: string;
  principalName: string;
  principalSurname: string;
  principalEmail: string;
  principalPhone: string;
  adminContactName: string;
  adminContactEmail: string;
  adminContactPhone: string;
  subscriptionPlan: string;
  agreedToTerms: boolean;
}

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape',
];

const SCHOOL_TYPES = ['Public School', 'Independent School', 'Combined School', 'Special Needs School'];
const PHASES = ['Foundation Phase (Grade R–3)', 'Intermediate Phase (Grade 4–6)', 'Senior Phase (Grade 7–9)', 'FET Phase (Grade 10–12)', 'Full Primary (Grade R–7)', 'Full Secondary (Grade 8–12)', 'Combined (Grade R–12)'];

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 'R 850',
    period: '/month',
    learners: 'Up to 300 learners',
    features: ['School Admin Portal', 'Teacher Portals', 'Parent & Learner Portal', 'Attendance Tracking', 'Basic Reports'],
    highlight: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 'R 1,850',
    period: '/month',
    learners: 'Up to 800 learners',
    features: ['Everything in Starter', 'Assessment & Grading', 'Behaviour Management', 'Homework Tracking', 'SMS Notifications', 'Advanced Analytics'],
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'R 3,500',
    period: '/month',
    learners: 'Unlimited learners',
    features: ['Everything in Growth', 'Custom Branding', 'API Access', 'Priority Support', 'Dedicated Account Manager', 'Data Export & Integrations'],
    highlight: false,
  },
];

const steps = [
  { id: 1, label: 'School Details', icon: <School size={16} /> },
  { id: 2, label: 'Principal & Contact', icon: <User size={16} /> },
  { id: 3, label: 'Subscription Plan', icon: <CreditCard size={16} /> },
  { id: 4, label: 'Review & Submit', icon: <CheckCircle2 size={16} /> },
];

export default function SchoolRegistrationClient() {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [selectedPlan, setSelectedPlan] = useState('growth');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<RegistrationFormData>({
    defaultValues: { subscriptionPlan: 'growth', agreedToTerms: false },
  });

  const watchedValues = watch();

  const nextStep = async () => {
    let fields: (keyof RegistrationFormData)[] = [];
    if (currentStep === 1) fields = ['schoolName', 'schoolType', 'phase', 'emisNumber', 'province', 'district', 'streetAddress', 'city', 'postalCode'];
    if (currentStep === 2) fields = ['principalName', 'principalSurname', 'principalEmail', 'principalPhone'];
    const valid = await trigger(fields);
    if (valid) setCurrentStep((s) => (s + 1) as Step);
  };

  const prevStep = () => setCurrentStep((s) => (s - 1) as Step);

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('schools').insert({
        name: watchedValues.schoolName,
        emis_number: watchedValues.emisNumber,
        province: watchedValues.province,
        principal_email: watchedValues.principalEmail,
        status: 'pending',
        school_type: watchedValues.schoolType,
        phase: watchedValues.phase,
        district: watchedValues.district,
        street_address: watchedValues.streetAddress,
        suburb: watchedValues.suburb || null,
        city: watchedValues.city,
        postal_code: watchedValues.postalCode,
        principal_name: watchedValues.principalName,
        principal_surname: watchedValues.principalSurname,
        principal_phone: watchedValues.principalPhone,
        admin_contact_name: watchedValues.adminContactName || null,
        admin_contact_email: watchedValues.adminContactEmail || null,
        admin_contact_phone: watchedValues.adminContactPhone || null,
        subscription_plan: selectedPlan,
      });

      if (error) {
        toast.error(error.message || 'Registration failed. Please try again.');
        return;
      }

      setSubmitted(true);
      toast.success('Registration submitted! LearnerLink SA will review and respond within 2 business days.');
    } catch (err: any) {
      toast.error(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full gradient-success flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={40} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">Registration Submitted!</h2>
          <p className="text-muted-foreground mb-2">
            <strong className="text-foreground">{watchedValues.schoolName || 'Your school'}</strong> has been submitted for review.
          </p>
          <p className="text-muted-foreground text-sm mb-8">
            The LearnerLink SA System Administrator will review your application and respond within 2 business days. You will receive a confirmation email at <strong>{watchedValues.principalEmail}</strong>.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 gradient-primary text-white rounded-lg font-600 text-sm hover:opacity-90 transition-opacity"
          >
            Return to Sign In
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppImage
              src="/assets/images/LearnerLink_SA-1783193395309.png"
              alt="LearnerLink SA logo"
              width={36}
              height={36}
              className="rounded-lg"
            />
            <div>
              <span className="font-bold text-base text-primary">LearnerLink SA</span>
              <p className="text-xs text-muted-foreground">School Registration</p>
            </div>
          </div>
          <a href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Already registered? Sign In
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Page title */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Register Your School</h1>
          <p className="text-muted-foreground text-sm">Complete the form below to apply for a LearnerLink SA account. All applications are reviewed by our System Administrator.</p>
        </div>

        {/* Step progress */}
        <div className="mb-8">
          <div className="flex items-center gap-0">
            {steps.map((step, idx) => (
              <React.Fragment key={`step-${step.id}`}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-700 transition-all duration-300 ${
                      currentStep > step.id
                        ? 'gradient-success text-white'
                        : currentStep === step.id
                        ? 'gradient-primary text-white shadow-card-md'
                        : 'bg-muted text-muted-foreground border border-border'
                    }`}
                  >
                    {currentStep > step.id ? <Check size={16} /> : step.id}
                  </div>
                  <p className={`text-xs mt-1.5 font-500 hidden sm:block ${currentStep === step.id ? 'text-primary' : 'text-muted-foreground'}`}>
                    {step.label}
                  </p>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`step-connector mx-2 mb-5 ${currentStep > step.id ? 'active' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form card */}
        <div className="bg-card rounded-2xl border border-border shadow-card p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1: School Details */}
            {currentStep === 1 && (
              <div className="fade-in space-y-6">
                <div>
                  <h3 className="text-lg font-700 text-foreground mb-1">School Details</h3>
                  <p className="text-sm text-muted-foreground">Basic information about your school organisation.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-600 text-foreground mb-1.5">School Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. St Mary's Primary School"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.schoolName ? 'border-danger' : 'border-border'}`}
                      {...register('schoolName', { required: 'School name is required' })}
                    />
                    {errors.schoolName && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.schoolName.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">School Type <span className="text-danger">*</span></label>
                    <select
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.schoolType ? 'border-danger' : 'border-border'}`}
                      {...register('schoolType', { required: 'School type is required' })}
                    >
                      <option value="">Select type...</option>
                      {SCHOOL_TYPES.map((t) => <option key={`type-${t}`} value={t}>{t}</option>)}
                    </select>
                    {errors.schoolType && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.schoolType.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">School Phase <span className="text-danger">*</span></label>
                    <select
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.phase ? 'border-danger' : 'border-border'}`}
                      {...register('phase', { required: 'Phase is required' })}
                    >
                      <option value="">Select phase...</option>
                      {PHASES.map((p) => <option key={`phase-${p}`} value={p}>{p}</option>)}
                    </select>
                    {errors.phase && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.phase.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">EMIS Number <span className="text-danger">*</span></label>
                    <p className="text-xs text-muted-foreground mb-1.5">Your school&apos;s Education Management Information System number from DBE.</p>
                    <input
                      type="text"
                      placeholder="e.g. GT-EM-000123"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.emisNumber ? 'border-danger' : 'border-border'}`}
                      {...register('emisNumber', {
                        required: 'EMIS number is required',
                        pattern: { value: /^[A-Z]{2}-[A-Z]{2}-\d{6}$/, message: 'Format: XX-XX-000000 (e.g. GT-EM-000123)' },
                      })}
                    />
                    {errors.emisNumber && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.emisNumber.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Province <span className="text-danger">*</span></label>
                    <select
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.province ? 'border-danger' : 'border-border'}`}
                      {...register('province', { required: 'Province is required' })}
                    >
                      <option value="">Select province...</option>
                      {SA_PROVINCES.map((p) => <option key={`prov-${p}`} value={p}>{p}</option>)}
                    </select>
                    {errors.province && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.province.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">District / Circuit <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Ekurhuleni North"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.district ? 'border-danger' : 'border-border'}`}
                      {...register('district', { required: 'District is required' })}
                    />
                    {errors.district && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.district.message}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-600 text-foreground mb-1.5">Street Address <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. 14 Oak Street"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.streetAddress ? 'border-danger' : 'border-border'}`}
                      {...register('streetAddress', { required: 'Street address is required' })}
                    />
                    {errors.streetAddress && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.streetAddress.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">City / Town <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Johannesburg"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.city ? 'border-danger' : 'border-border'}`}
                      {...register('city', { required: 'City is required' })}
                    />
                    {errors.city && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.city.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Postal Code <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. 1459"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.postalCode ? 'border-danger' : 'border-border'}`}
                      {...register('postalCode', {
                        required: 'Postal code is required',
                        pattern: { value: /^\d{4}$/, message: 'SA postal codes are 4 digits' },
                      })}
                    />
                    {errors.postalCode && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.postalCode.message}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Principal & Contact */}
            {currentStep === 2 && (
              <div className="fade-in space-y-6">
                <div>
                  <h3 className="text-lg font-700 text-foreground mb-1">Principal & Administrative Contact</h3>
                  <p className="text-sm text-muted-foreground">Contact details for the school principal and primary admin account holder.</p>
                </div>
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 mb-2">
                  <p className="text-xs font-600 text-primary flex items-center gap-1.5"><User size={12} /> The principal email will be used to create the initial School Admin login.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Principal First Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Thandi"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.principalName ? 'border-danger' : 'border-border'}`}
                      {...register('principalName', { required: 'First name is required' })}
                    />
                    {errors.principalName && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.principalName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Principal Surname <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Molefe"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.principalSurname ? 'border-danger' : 'border-border'}`}
                      {...register('principalSurname', { required: 'Surname is required' })}
                    />
                    {errors.principalSurname && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.principalSurname.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Principal Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      placeholder="principal@yourschool.edu.za"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.principalEmail ? 'border-danger' : 'border-border'}`}
                      {...register('principalEmail', {
                        required: 'Email is required',
                        pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                      })}
                    />
                    {errors.principalEmail && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.principalEmail.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Principal Phone <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      placeholder="e.g. 011 234 5678"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 ${errors.principalPhone ? 'border-danger' : 'border-border'}`}
                      {...register('principalPhone', { required: 'Phone number is required' })}
                    />
                    {errors.principalPhone && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.principalPhone.message}</p>}
                  </div>

                  <div className="md:col-span-2 border-t border-border pt-4">
                    <p className="text-sm font-600 text-foreground mb-4">Administrative Contact (optional — if different from principal)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Admin Contact Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Bongani Khumalo"
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                      {...register('adminContactName')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Admin Contact Email</label>
                    <input
                      type="email"
                      placeholder="admin@yourschool.edu.za"
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                      {...register('adminContactEmail')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">Admin Contact Phone</label>
                    <input
                      type="tel"
                      placeholder="e.g. 082 345 6789"
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                      {...register('adminContactPhone')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Subscription Plan */}
            {currentStep === 3 && (
              <div className="fade-in space-y-6">
                <div>
                  <h3 className="text-lg font-700 text-foreground mb-1">Choose Your Subscription Plan</h3>
                  <p className="text-sm text-muted-foreground">All plans include a 30-day free trial. No credit card required to register.</p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {PLANS.map((plan) => (
                    <button
                      key={`plan-${plan.id}`}
                      type="button"
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`relative text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                        selectedPlan === plan.id
                          ? 'border-primary bg-primary/5 shadow-card-md'
                          : 'border-border bg-card hover:border-primary/40'
                      }`}
                    >
                      {plan.highlight && (
                        <span className="absolute -top-3 left-5 px-3 py-1 gradient-primary text-white text-xs font-700 rounded-full">Most Popular</span>
                      )}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-700 text-foreground">{plan.name}</p>
                            <span className="text-xs text-muted-foreground">{plan.learners}</span>
                          </div>
                          <div className="flex items-baseline gap-1 mb-3">
                            <span className="text-2xl font-800 text-primary font-tabular">{plan.price}</span>
                            <span className="text-sm text-muted-foreground">{plan.period}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            {plan.features.map((f) => (
                              <p key={`feat-${plan.id}-${f}`} className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <Check size={11} className="text-success shrink-0" />
                                {f}
                              </p>
                            ))}
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 shrink-0 mt-1 flex items-center justify-center transition-all ${selectedPlan === plan.id ? 'border-primary bg-primary' : 'border-border'}`}>
                          {selectedPlan === plan.id && <Check size={12} className="text-white" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="p-4 rounded-xl bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-foreground">30-day free trial</strong> — Your school will not be billed until after approval and trial completion. Pricing excludes VAT.
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {currentStep === 4 && (
              <div className="fade-in space-y-6">
                <div>
                  <h3 className="text-lg font-700 text-foreground mb-1">Review & Submit</h3>
                  <p className="text-sm text-muted-foreground">Please review your registration details before submitting.</p>
                </div>
                <div className="space-y-4">
                  {[
                    {
                      title: 'School Details',
                      icon: <Building2 size={14} />,
                      items: [
                        { label: 'School Name', value: watchedValues.schoolName || '—' },
                        { label: 'Type', value: watchedValues.schoolType || '—' },
                        { label: 'Phase', value: watchedValues.phase || '—' },
                        { label: 'EMIS Number', value: watchedValues.emisNumber || '—' },
                        { label: 'Province', value: watchedValues.province || '—' },
                        { label: 'District', value: watchedValues.district || '—' },
                        { label: 'Address', value: watchedValues.streetAddress ? `${watchedValues.streetAddress}, ${watchedValues.city}, ${watchedValues.postalCode}` : '—' },
                      ],
                    },
                    {
                      title: 'Principal Contact',
                      icon: <User size={14} />,
                      items: [
                        { label: 'Principal', value: `${watchedValues.principalName || ''} ${watchedValues.principalSurname || ''}`.trim() || '—' },
                        { label: 'Email', value: watchedValues.principalEmail || '—' },
                        { label: 'Phone', value: watchedValues.principalPhone || '—' },
                      ],
                    },
                    {
                      title: 'Subscription Plan',
                      icon: <CreditCard size={14} />,
                      items: [
                        { label: 'Selected Plan', value: PLANS.find((p) => p.id === selectedPlan)?.name || '—' },
                        { label: 'Monthly Price', value: PLANS.find((p) => p.id === selectedPlan)?.price || '—' },
                        { label: 'Learner Limit', value: PLANS.find((p) => p.id === selectedPlan)?.learners || '—' },
                      ],
                    },
                  ].map((section) => (
                    <div key={`review-${section.title}`} className="rounded-xl border border-border overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b border-border">
                        <span className="text-primary">{section.icon}</span>
                        <p className="text-sm font-700 text-foreground">{section.title}</p>
                      </div>
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {section.items.map((item) => (
                          <div key={`review-item-${section.title}-${item.label}`}>
                            <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                            <p className="text-sm font-500 text-foreground">{item.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card">
                  <input
                    type="checkbox"
                    id="agreedToTerms"
                    className="mt-0.5 w-4 h-4 accent-primary"
                    {...register('agreedToTerms', { required: 'You must agree to the terms to proceed' })}
                  />
                  <label htmlFor="agreedToTerms" className="text-sm text-muted-foreground cursor-pointer">
                    I confirm that the information provided is accurate and that I have authority to register this school. I agree to the{' '}
                    <span className="text-primary font-600">Terms of Service</span> and{' '}
                    <span className="text-primary font-600">Privacy Policy</span>.
                  </label>
                </div>
                {errors.agreedToTerms && <p className="text-xs text-danger flex items-center gap-1"><AlertCircle size={11} />{errors.agreedToTerms.message}</p>}
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border text-muted-foreground text-sm font-600 hover:bg-muted/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground">Step {currentStep} of 4</p>
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg gradient-primary text-white text-sm font-600 hover:opacity-90 active:scale-95 transition-all"
                  >
                    Continue
                    <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg gradient-success text-white text-sm font-600 hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} />
                        Submit Registration
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}